export const javascriptQuestions = [
  {
    id: 1,
    question: "What are Promises and async/await?",
    parts: [
      {
        type: "text",
        content: `**Promises** represent eventual completion (or failure) of async operations.

**Promise States**
- ⏳ pending
- ✅ fulfilled
- ❌ rejected

**Methods**
- \`then\`, \`catch\`, \`finally\`

**async/await** is syntax sugar over Promises.
- ✨ \`async\` functions return a Promise
- 🛑 \`await\` pauses until Promise settles
- 📖 Improves readability of async flows`
      },
      {
        type: "code",
        language: "javascript",
        content: "fetch(url)\n  .then(response => response.json())\n  .then(data => console.log(data))\n  .catch(error => console.error(error));\n\nasync function fetchData() {\n  try {\n    const response = await fetch(url);\n    const data = await response.json();\n    console.log(data);\n  } catch (error) {\n    console.error(error);\n  }\n}"
      }
    ]
  },
  {
    id: 2,
    question: "Explain 'this' keyword in JavaScript",
    parts: [
      {
        type: "text",
        content: `**this** refers to the execution context and depends on how a function is called.

**Bindings**
- 🌍 Global: \`window\` (browser) / \`global\` (Node)
- 🧩 Method call: the object before the dot
- 🆕 Constructor: the newly created instance
- 🏹 Arrow functions: lexical \`this\` (from outer scope)
- 🎛️ Event handlers: the event target element
- 🔗 Explicit: \`call\`, \`apply\`, \`bind\`

**Tips**
- Use arrow functions or \`bind\` to avoid losing \`this\``
      }
    ]
  },
  {
    id: 3,
    question: "What is the difference between == and ===?",
    parts: [
      {
        type: "text",
        content: `**== vs ===**

**== (Loose Equality)**
- 🔄 Performs type coercion
- Example: \`5 == '5'\` → \`true\`

**=== (Strict Equality)**
- ✅ Compares value and type
- 🧭 Predictable, no coercion

**Best Practice**
- Prefer \`===\` / \`!==\`
- Exception: \`x == null\` matches \`null\` or \`undefined\``
      }
    ]
  },
  {
    id: 4,
    question: "What is a compiler vs transpiler?",
    parts: [
      {
        type: "text",
        content: `**Compiler vs Transpiler**

Compiler:
- Converts source to lower-level output (machine/bytecode)

Transpiler:
- Converts source to source at the same abstraction level (e.g., TS → JS, ES6 → ES5)

Web tooling:
- Babel: transpiles modern JS/JSX
- TypeScript compiler (tsc): transpiles TS and type-checks
- Sass → CSS
- CoffeeScript → JS`
      }
    ]
  },
  {
    id: 5,
    question: "What is the difference between Promise.all and Promise.race?",
    parts: [
      {
        type: "text",
        content: `**Promise.all vs Promise.race**

Promise.all:
- Waits for all promises to settle
- Resolves with array of results
- Rejects if any promise rejects

Promise.race:
- Resolves/rejects with the first settled promise

Use cases:
- all: multiple required resources
- race: timeouts/fastest response

Related:
- Promise.allSettled, Promise.any`
      },
      {
        type: "code",
        language: "javascript",
        content: "Promise.all([fetch(url1), fetch(url2)])\n  .then(([r1, r2]) => {/* ... */})\n  .catch(err => {/* any failed */});\n\nPromise.race([fetch(url1), fetch(url2)])\n  .then(result => {/* fastest */});"
      }
    ]
  },
  {
    id: 6,
    question: "How to handle async calls when each depends on the previous response?",
    parts: [
      {
        type: "text",
        content: `**Dependent Async Chains**

- Prefer async/await for sequential dependencies
- Parallelize independent calls with Promise.all
- Add per-step error handling
- Build dynamic chains via Array.reduce`
      },
      {
        type: "code",
        language: "javascript",
        content: "async function fetchUserData(userId) {\n  const user = await fetch('/api/users/' + userId).then(r => r.json());\n  const posts = await fetch('/api/posts?user=' + user.id).then(r => r.json());\n  const comments = await fetch('/api/comments?post=' + posts[0].id).then(r => r.json());\n  return { user, posts, comments };\n}"
      },
      {
        type: "code",
        language: "javascript",
        content: "const urls = [url1, url2, url3];\nurls.reduce((p, url) =>\n  p\n    .then(res => fetch(url).then(r => r.json()))\n    .then(d => [...res, d]),\n  Promise.resolve([])\n);"
      }
    ]
  },
  {
    id: 7,
    question: "What techniques make a fetch call stop after some time of inactivity?",
    parts: [
      {
        type: "text",
        content: `**Fetch Timeout Techniques**

- AbortController with setTimeout
- Promise.race with a timeout promise
- Helper to wrap a promise with timeout
- Axios has built-in timeout
- React hooks pattern for cleanup
- Retries with backoff

Tip: Always set timeouts for external calls.`
      },
      {
        type: "code",
        language: "javascript",
        content: "async function fetchWithTimeout(url, timeout = 5000) {\n  const controller = new AbortController();\n  const id = setTimeout(() => controller.abort(), timeout);\n  try {\n    const res = await fetch(url, { signal: controller.signal });\n    clearTimeout(id);\n    return await res.json();\n  } catch (e) {\n    if (e.name === 'AbortError') throw new Error('Request timeout');\n    throw e;\n  }\n}"
      }
    ]
  },
  {
    id: 9,
    question: "What is webpack/vite and why do we use bundlers?",
    parts: [
      {
        type: "text",
        content: `**Bundlers (webpack/vite)**

Bundlers combine code and dependencies into optimized files for the browser.

What is a bundle:
- One or a few files containing all app code, instead of hundreds of separate files.

Why use them:
1) Performance:
- Fewer HTTP requests (100 files → 1–3 bundles)
- Faster page load

2) Code transformation:
- Transpile modern JS (Babel)
- Process CSS, images, fonts
- Minify and optimize

3) Dev experience:
- Hot Module Replacement (instant updates)
- Dev server
- Source maps for debugging

4) Optimization:
- Tree-shaking removes unused code
- Code splitting for lazy loading
- Asset compression

Webpack vs Vite:
- Webpack: Older, highly configurable, complex
- Vite: Modern, faster, simpler setup

Modern practice: Use Vite for new projects; webpack for complex needs.`
      }
    ]
  },
  {
    id: 10,
    question: "What types in JavaScript are compared and passed by reference?",
    parts: [
      {
        type: "text",
        content: `**Value vs Reference in JavaScript**

JavaScript has two categories:

Primitives (passed/compared by VALUE):
1. string
2. number
3. boolean
4. undefined
5. null
6. symbol
7. bigint

Objects (passed/compared by REFERENCE):
1. Object
2. Array
3. Function
4. Date
5. RegExp
6. Map, Set, WeakMap, WeakSet
7. Any other object type

Where reference points to:
- Heap memory
- Object data stored in heap
- Variable holds memory address

Implications:
1) Mutation affects all references
2) Comparison checks reference equality
3) Function parameters pass references for objects
4) React state: avoid in-place mutation; create new objects
5) Array methods: copying vs referencing

Copying objects:
- Shallow: spread, Object.assign
- Deep: JSON.parse(JSON.stringify()), structuredClone, lodash cloneDeep

Note: const prevents reassignment, not mutation.`
      },
      {
        type: "code",
        language: "javascript",
        content: "let a = 5;\nlet b = a; // Copy value\nb = 10;\nconsole.log(a); // Still 5\n\nlet str1 = 'hello';\nlet str2 = 'hello';\nconsole.log(str1 === str2); // true (same value)"
      },
      {
        type: "code",
        language: "javascript",
        content: "let obj1 = { name: 'John' };\nlet obj2 = obj1; // Copy reference (same object)\nobj2.name = 'Jane';\nconsole.log(obj1.name); // 'Jane'\n\nlet arr1 = [1, 2, 3];\nlet arr2 = [1, 2, 3];\nconsole.log(arr1 === arr2); // false (different references)"
      },
      {
        type: "code",
        language: "javascript",
        content: "const user = { name: 'John' };\nconst admin = user;\nadmin.name = 'Jane';\nconsole.log(user.name); // 'Jane'"
      },
      {
        type: "code",
        language: "javascript",
        content: "const a = { x: 1 };\nconst b = { x: 1 };\nconsole.log(a === b); // false\nconst c = a;\nconsole.log(a === c); // true"
      },
      {
        type: "code",
        language: "javascript",
        content: "function change(obj) {\n  obj.name = 'Changed'; // Modifies original\n}\n\nconst user = { name: 'John' };\nchange(user);\nconsole.log(user.name); // 'Changed'"
      },
      {
        type: "code",
        language: "javascript",
        content: "// React state pitfall\n// Wrong\nconst user = state.user;\nuser.name = 'Jane';\nsetState({ user }); // React may not detect change!\n\n// Correct\nsetState({ user: { ...state.user, name: 'Jane' } });"
      },
      {
        type: "code",
        language: "javascript",
        content: "const original = [1, 2, 3];\nconst copy1 = original; // Reference\nconst copy2 = [...original]; // New array\nconst copy3 = original.slice(); // New array\n\ncopy1.push(4);\nconsole.log(original); // [1, 2, 3, 4]\n\ncopy2.push(5);\nconsole.log(original); // [1, 2, 3, 4] (not affected)"
      },
      {
        type: "code",
        language: "javascript",
        content: "// Shallow copy\nconst copyA = { ...original };\nconst copyB = Object.assign({}, original);\n\n// Deep copy\nconst deep1 = JSON.parse(JSON.stringify(original));\nconst deep2 = structuredClone(original);\n\n// const and mutation\nconst obj = { x: 1 };\n// obj = {}; // Error (reassign)\nobj.x = 2; // OK (mutate)"
      }
    ]
  },
  {
    id: 11,
    question: "What happens with JavaScript sort() without parameters?",
    parts: [
      {
        type: "text",
        content: `**Array.sort() without compare**

- Converts elements to strings and sorts lexicographically.

Problem with numbers:
- '10' comes before '2' because '1' < '2'.

Best practice:
- Always provide a numeric compare function for numbers.

Important:
- sort() mutates the original array; copy first if needed.`
      },
      {
        type: "code",
        language: "javascript",
        content: "const numbers = [1, 5, 10, 25, 100];\nnumbers.sort();\n// Result: [1, 10, 100, 25, 5] ⚠️ Wrong!"
      },
      {
        type: "code",
        language: "javascript",
        content: "// Correct numeric sort\nconst asc = [...numbers].sort((a, b) => a - b); // [1, 5, 10, 25, 100]\nconst desc = [...numbers].sort((a, b) => b - a); // [100, 25, 10, 5, 1]"
      },
      {
        type: "code",
        language: "javascript",
        content: "// Strings\nitems.sort((a, b) => a.name.localeCompare(b.name));\n\n// Objects\nusers.sort((a, b) => a.age - b.age);"
      }
    ]
  },
  {
    id: 12,
    question: "Explain closures in JavaScript with an example",
    parts: [
      {
        type: "text",
        content: `**Closures** allow a function to access variables from its defining scope even after that scope has finished executing.

**Why They Matter**
- 🔒 Data privacy and encapsulation
- 🔁 Maintain state without globals
- 🧩 Functional patterns (currying, partial application)
`
      },
      {
        type: "code",
        language: "javascript",
        content: "function makeCounter() {\n  let count = 0;\n  return function () {\n    count++;\n    return count;\n  };\n}\n\nconst counter = makeCounter();\nconsole.log(counter()); // 1\nconsole.log(counter()); // 2"
      }
    ]
  },

  {
    id: 13,
    question: "How does the JavaScript event loop work?",
    parts: [
      {
        type: "text",
        content: `**Event Loop** enables async behavior in JavaScript's single-threaded runtime.

**Components**
- 🧠 Call Stack
- 🌐 Web APIs (timers, fetch, DOM)
- 📥 Callback (Task) Queue
- ⚡ Microtask Queue (Promises, MutationObserver)
- 🔁 Event Loop (dispatcher)

**Order**
- Microtasks run before macrotasks
- Prevents blocking and improves responsiveness
`
      },
      {
        type: "code",
        language: "javascript",
        content: "console.log('A');\nsetTimeout(() => console.log('B'), 0);\nPromise.resolve().then(() => console.log('C'));\nconsole.log('D');\n// Output: A, D, C, B"
      }
    ]
  },

  {
    id: 14,
    question: "Values vs references in JavaScript",
    parts: [
      {
        type: "text",
        content: `**Primitives** copy by value. **Objects/arrays/functions** copy by reference.

Pitfalls:
- Mutating a referenced object affects all references
- React state requires immutable updates
`
      },
      {
        type: "code",
        language: "javascript",
        content: "let a = 5;\nlet b = a; // Copy value\nb = 10;\nconsole.log(a); // Still 5\n\nlet obj1 = { name: 'John' };\nlet obj2 = obj1; // Copy reference\nobj2.name = 'Jane';\nconsole.log(obj1.name); // 'Jane'\n\n// Immutable update (React)\nsetState({ user: { ...state.user, name: 'Jane' } });"
      }
    ]
  },

  {
    id: 15,
    question: "Why should you pass a compare function to Array.sort()?",
    parts: [
      {
        type: "text",
        content: `**Array.sort()** without a compare function sorts lexicographically by string.

For numbers, this yields wrong ordering (e.g., '10' before '2').

Important:
- \`sort()\` mutates the original array; copy first if needed.
`
      },
      {
        type: "code",
        language: "javascript",
        content: "const numbers = [1, 5, 10, 25, 100];\nnumbers.sort();\n// Result: [1, 10, 100, 25, 5] // Wrong\n\n// Correct numeric sort\nconst asc = [...numbers].sort((a, b) => a - b);\nconst desc = [...numbers].sort((a, b) => b - a);\n\n// Strings\nitems.sort((a, b) => a.localeCompare(b));\n// Objects\nusers.sort((a, b) => a.age - b.age);"
      }
    ]
  },

  {
    id: 16,
    question: "When will the garbage collector activate for an object bound to the browser window?",
    parts: [
      {
        type: "text",
        content: `**GC for window-bound objects**

Short answer: Not until page unload.

Why:
- \`window\` exists for entire page lifetime
- Properties on \`window\` are global references
- GC collects only objects with no references

Proper cleanup:
- Delete the property or set it to null when done.

Common leak sources and fixes:
1) Event listeners: remove with removeEventListener
2) Intervals/timeouts: clear with clearInterval/clearTimeout
3) Globals: avoid or clean up
4) Detached DOM nodes: remove references
5) Closures capturing large data: release references

When GC does work:
- Local scope variables after function returns
- Module-scoped data when no references remain

Best practices:
- Avoid globals on \`window\`
- Clean up in component unmount/effects
- Use WeakMap for element metadata
- Check for leaks with DevTools Memory profiler
`
      },
      {
        type: "code",
        language: "javascript",
        content: "// Example: global reference prevents GC\nwindow.myData = { large: 'data' };\n// Eligible for GC only after cleanup\nfunction cleanup() {\n  delete window.myData; // or: window.myData = null;\n}"
      },
      {
        type: "code",
        language: "javascript",
        content: "// Memory leak via interval\nconst id = setInterval(() => {\n  // Keeps closure alive\n}, 1000);\n// Fix\nclearInterval(id);"
      },
      {
        type: "code",
        language: "javascript",
        content: "// React effect cleanup\nuseEffect(() => {\n  const handler = () => {/* ... */};\n  window.addEventListener('resize', handler);\n  return () => {\n    window.removeEventListener('resize', handler);\n  };\n}, []);"
      },
      {
        type: "code",
        language: "javascript",
        content: "// WeakMap for element metadata\nconst elementData = new WeakMap();\nelements.forEach(el => {\n  elementData.set(el, { metadata: {} });\n});"
      }
    ]
  }
];

